/**
 * F-007 — Collision routing.
 *
 * cannon-es emits a 'collide' event from each Body when contact occurs.
 * Both bodies fire the event for the same pair, so we dedup by sorted id
 * (BR-001) and only forward "same-tier" pairs to the merge queue (F-008
 * consumes from the queue).
 */

import type { Body } from "cannon-es";

import type { Planet } from "./planet.ts";

export interface MergeCandidate {
  a: Planet;
  b: Planet;
  tierIndex: number;
}

export interface ImpactEvent {
  /** Stronger of the two participants by tier — drives spark color/size. */
  primary: Planet;
  /** Other body in the contact pair. */
  secondary: Planet;
  /** Approximate world-space contact point. Midpoint of the two bodies. */
  point: { x: number; y: number; z: number };
  /** Approximate normal-axis impact speed (length of relative velocity). */
  speed: number;
  /** True when both planets share the same tier (merge candidate). */
  isMerge: boolean;
}

export type ImpactListener = (event: ImpactEvent) => void;

export interface CollisionRouter {
  /** Watch a planet's body for collisions. Idempotent per planet. */
  observe(planet: Planet): void;
  /** Stop watching (called on destroy / clear). */
  forget(planet: Planet): void;
  /** Drain merge candidates accumulated since the last drain. */
  drain(): MergeCandidate[];
  /** Subscribe to ANY planet-planet contact (merge OR cross-tier bounce). */
  onImpact(listener: ImpactListener): () => void;
  /** Hint for the test suite + diagnostics. */
  pendingCount(): number;
}

interface CollideEvent {
  body: Body;
}

export function createCollisionRouter(): CollisionRouter {
  const impactListeners = new Set<ImpactListener>();
  const planetByBodyId = new Map<number, Planet>();
  const handlers = new Map<number, (e: CollideEvent) => void>();
  // Dedup pair keys for events fired in the same tick by both bodies.
  const seenPairs = new Set<string>();
  const pending: MergeCandidate[] = [];

  const pairKey = (a: Planet, b: Planet): string => {
    const lo = Math.min(a.id, b.id);
    const hi = Math.max(a.id, b.id);
    return `${lo}:${hi}`;
  };

  const observe = (planet: Planet): void => {
    if (handlers.has(planet.body.id)) return;
    planetByBodyId.set(planet.body.id, planet);

    const handler = (e: CollideEvent): void => {
      const other = planetByBodyId.get(e.body.id);
      if (!other || other === planet) return;

      const key = pairKey(planet, other);
      if (seenPairs.has(key)) return;
      seenPairs.add(key);

      // Notify impact listeners for ANY contact — drives spark FX.
      // Merge resolution is still gated on same-tier below.
      if (impactListeners.size > 0) {
        const ap = planet.body.position;
        const bp = other.body.position;
        const av = planet.body.velocity;
        const bv = other.body.velocity;
        const dvx = av.x - bv.x;
        const dvy = av.y - bv.y;
        const dvz = av.z - bv.z;
        const isMerge = other.tier.index === planet.tier.index;
        const primary = planet.tier.index >= other.tier.index ? planet : other;
        const secondary = primary === planet ? other : planet;
        const impact: ImpactEvent = {
          primary,
          secondary,
          point: {
            x: (ap.x + bp.x) * 0.5,
            y: (ap.y + bp.y) * 0.5,
            z: (ap.z + bp.z) * 0.5,
          },
          speed: Math.hypot(dvx, dvy, dvz),
          isMerge,
        };
        for (const listener of impactListeners) listener(impact);
      }

      if (other.tier.index !== planet.tier.index) return;
      pending.push({ a: planet, b: other, tierIndex: planet.tier.index });
    };

    planet.body.addEventListener("collide", handler);
    handlers.set(planet.body.id, handler);
  };

  const forget = (planet: Planet): void => {
    const handler = handlers.get(planet.body.id);
    if (handler) {
      planet.body.removeEventListener("collide", handler);
      handlers.delete(planet.body.id);
    }
    planetByBodyId.delete(planet.body.id);
  };

  const drain = (): MergeCandidate[] => {
    const out = pending.splice(0, pending.length);
    seenPairs.clear();
    return out;
  };

  const pendingCount = (): number => pending.length;

  const onImpact = (listener: ImpactListener): (() => void) => {
    impactListeners.add(listener);
    return () => impactListeners.delete(listener);
  };

  return { observe, forget, drain, onImpact, pendingCount };
}
